import cn from 'classnames'
import {
  Button,
  CategoryIcon,
  isCategorySlug,
  SkillIcon,
  isSkillSlug,
  IconStarDefault,
} from '@mc/design-system'
import { TrackingContainer } from '@mc/visibility'
import { trackCategoryPickerViewed } from './events'
import styles from './FilterPicker.module.scss'

export type FilterPickerProps = {
  selections: Array<Selection>
  selectedSlug: string
  setSelectedSlug(slug: string): void
  onSelect?(slug: string, id?: string): void
  /**
   * Bootstrap utility classes controlling horizontal alignment & margins
   * on ≥sm. Defaults match the homepage layout. Pass
   * `'justify-content-sm-start'` for a left-aligned variant without
   * extra container margin.
   */
  alignmentClassName?: string
}

export type Selection = { name: string; slug: string; id?: string }

export const FilterPicker = ({
  selections,
  selectedSlug,
  setSelectedSlug,
  onSelect,
  alignmentClassName = 'justify-content-sm-center mc-mx-xl-5',
}: FilterPickerProps) => (
  <TrackingContainer onFirstVisible={trackCategoryPickerViewed}>
    <div
      className={cn(
        'mc-my-5 mc-py-1',
        'd-flex flex-nowrap flex-sm-wrap',
        alignmentClassName,
        styles.categoryPicker,
      )}
    >
      {selections.map((selection) => (
        <FilterButton
          key={selection.slug}
          selection={selection}
          isSelected={selection.slug === selectedSlug}
          onSelect={(slug: string, id: string) => {
            if (onSelect) {
              onSelect(slug, id)
            }
            setSelectedSlug(slug)
          }}
        />
      ))}
    </div>
  </TrackingContainer>
)

type FilterButtonProps = {
  selection: Selection
  isSelected: boolean
  onSelect(slug: string, id?: string): void
}

const getIcon = (slug: string): React.ReactNode => {
  if (slug === 'trending') return <IconStarDefault />
  if (isCategorySlug(slug)) return <CategoryIcon slug={slug} />
  if (isSkillSlug(slug)) return <SkillIcon slug={slug} />
  return null
}

const FilterButton = ({
  selection,
  isSelected,
  onSelect,
}: FilterButtonProps) => {
  const icon = getIcon(selection.slug)

  return (
    <Button
      onClick={() => onSelect(selection.slug, selection.id)}
      kind='secondary'
      size='md'
      className={cn(
        'd-sm-flex mc-p-2 mc-py-sm-3 mc-px-sm-4',
        styles.filterButton,
        isSelected && styles.active,
      )}
    >
      {icon && (
        <span className={cn('mc-mr-3 d-none d-lg-block', styles.icon)}>
          {icon}
        </span>
      )}
      {selection.name}
    </Button>
  )
}
